import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Briefcase, MapPin, Clock, DollarSign, ChevronRight, Loader2, Building2,
  Heart, Zap, Shield, Sun, Search, Star
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SEOHead } from '@/components/seo/SEOHead';
import { PageHero } from '@/components/common/PageHero';
import { ConfigurableHero } from '@/components/common/ConfigurableHero';
import { usePageDesign } from '@/hooks/usePageDesign';

interface Job {
  id: string;
  title: string;
  department: string | null;
  location: string | null;
  employment_type: string | null;
  salary_range: string | null;
  description: string | null;
  published_at: string | null;
}

const slugify = (text: string) =>
  text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const benefits = [
  { icon: Heart, title: 'Great Culture', description: 'Work with a supportive team that values collaboration and growth.' },
  { icon: Zap, title: 'Career Growth', description: 'Opportunities for advancement and professional development.' },
  { icon: Shield, title: 'Benefits Package', description: 'Competitive salary, health insurance, and retirement plans.' },
  { icon: Sun, title: 'Work-Life Balance', description: 'Flexible schedules and paid time off to recharge.' },
];

export default function CareersPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDepartment, setFilterDepartment] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const { heroStyle, showBreadcrumbs, showTrustBadges, showCtaButtons } = usePageDesign('jobs');

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['public-jobs'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('jobs')
        .select('*')
        .eq('is_published', true)
        .order('published_at', { ascending: false });
      if (error) throw error;
      return data as Job[];
    }
  });

  const departments = useMemo(() => {
    const depts = new Set(jobs.map(j => j.department || 'General'));
    return Array.from(depts).sort();
  }, [jobs]);

  const employmentTypes = useMemo(() => {
    const types = new Set(jobs.filter(j => j.employment_type).map(j => j.employment_type!));
    return Array.from(types).sort();
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    return jobs.filter(job => {
      const matchesSearch = !searchQuery.trim() || 
        job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (job.department || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (job.location || '').toLowerCase().includes(searchQuery.toLowerCase());
      const matchesDept = filterDepartment === 'all' || (job.department || 'General') === filterDepartment;
      const matchesType = filterType === 'all' || job.employment_type === filterType;
      return matchesSearch && matchesDept && matchesType;
    });
  }, [jobs, searchQuery, filterDepartment, filterType]);

  // Featured = first 2 jobs; rest = all positions
  const featuredJobs = filteredJobs.slice(0, 2);
  const allJobs = filteredJobs.slice(2);

  return (
    <>
      <SEOHead
        title="Careers - Join Our Team"
        description="Explore exciting career opportunities. We're always looking for talented individuals to join our growing team."
      />
      
      <div className="min-h-screen">
        <ConfigurableHero
          heroStyle={heroStyle}
          title="Join Our Team"
          subtitle="Build your career with us. We're looking for talented individuals who are passionate about making a difference."
          badge="We're Hiring"
          showBreadcrumbs={showBreadcrumbs}
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Careers' }]}
          showTrustBadges={showTrustBadges}
          showCtaButtons={showCtaButtons}
        />

        {/* Stats bar */}
        {jobs.length > 0 && (
          <div className="flex justify-center gap-6 -mt-6 mb-2 relative z-10">
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <Building2 className="h-4 w-4" /> {jobs.length} Open Position{jobs.length !== 1 ? 's' : ''}
            </span>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              <MapPin className="h-4 w-4" /> Multiple Locations
            </span>
          </div>
        )}

        {/* Why Join Us */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold text-center mb-10">Why Join Us?</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, i) => (
                <motion.div
                  key={benefit.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-primary/10 mb-4">
                    <benefit.icon className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl font-bold mb-6">Open Positions</h2>

            {/* Search & Filters */}
            <div className="flex flex-col sm:flex-row gap-3 mb-8">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search jobs..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Select value={filterDepartment} onValueChange={setFilterDepartment}>
                <SelectTrigger className="w-full sm:w-[180px]">
                  <SelectValue placeholder="All Departments" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Departments</SelectItem>
                  {departments.map(d => (
                    <SelectItem key={d} value={d}>{d}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-full sm:w-[160px]">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {employmentTypes.map(t => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : filteredJobs.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                  <p className="text-muted-foreground">
                    {jobs.length === 0 ? 'No open positions at the moment. Check back soon!' : 'No positions match your search.'}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-10">
                {/* Featured Positions */}
                {featuredJobs.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-4">
                      <Star className="h-5 w-5 text-primary fill-primary" />
                      <h3 className="font-semibold text-xl">Featured Positions</h3>
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      {featuredJobs.map((job, index) => (
                        <motion.div
                          key={job.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.1 }}
                        >
                          <Link to={`/careers/${slugify(job.title)}`}>
                            <Card className="h-full hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group border-primary/10">
                              <CardContent className="p-6">
                                <div className="flex items-start justify-between gap-2 mb-3">
                                  <h4 className="font-bold text-lg group-hover:text-primary transition-colors">
                                    {job.title}
                                  </h4>
                                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0 mt-0.5" />
                                </div>
                                <div className="flex flex-wrap gap-2 mb-3">
                                  {job.department && <Badge variant="secondary" className="text-xs">{job.department}</Badge>}
                                  {job.employment_type && <Badge variant="outline" className="text-xs">{job.employment_type}</Badge>}
                                </div>
                                {job.description && (
                                  <p className="text-sm text-muted-foreground line-clamp-3 mb-4">{job.description}</p>
                                )}
                                <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mt-auto">
                                  {job.location && (
                                    <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
                                  )}
                                  {job.salary_range && (
                                    <span className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{job.salary_range}</span>
                                  )}
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}

                {/* All Positions */}
                {allJobs.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-xl mb-4">All Positions</h3>
                    <div className="grid gap-3">
                      {allJobs.map((job, index) => (
                        <motion.div
                          key={job.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: index * 0.05 }}
                        >
                          <Link to={`/careers/${slugify(job.title)}`}>
                            <Card className="hover:shadow-lg hover:border-primary/30 transition-all cursor-pointer group">
                              <CardContent className="p-5">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start gap-3">
                                      <div className="p-2 bg-primary/10 rounded-lg shrink-0">
                                        <Briefcase className="h-5 w-5 text-primary" />
                                      </div>
                                      <div className="min-w-0">
                                        <h4 className="font-semibold text-lg group-hover:text-primary transition-colors truncate">
                                          {job.title}
                                        </h4>
                                        <div className="flex flex-wrap gap-2 mt-1">
                                          {job.department && <Badge variant="secondary" className="text-xs">{job.department}</Badge>}
                                          {job.employment_type && <Badge variant="outline" className="text-xs">{job.employment_type}</Badge>}
                                        </div>
                                        {job.description && (
                                          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">{job.description}</p>
                                        )}
                                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-muted-foreground">
                                          {job.location && (
                                            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{job.location}</span>
                                          )}
                                          {job.salary_range && (
                                            <span className="flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />{job.salary_range}</span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                  <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all shrink-0" />
                                </div>
                              </CardContent>
                            </Card>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </>
  );
}